import { useState, ChangeEvent, useEffect } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { handleEnterKey } from "../../app/keyboardUtils";
import { DataTablesViewModel } from "../../interfaces/dataTables";
import NoDataTableBody from "../../components/NoDataTableBody";
import { SimpleOption } from "../../components/input/SimpleListbox";
import TextWithTip from "../../components/TextWithTip";
import StepProgressBar, { StepStatus } from "../../components/StepProgressBar";
import Select from "../../components/input/Select";
import SearchFilter from "../../components/input/SearchFilter";
import { useGetGradesQuery } from "../lookup/lookupApiSlice";
import {
	SelectQueryModel,
	useGetCategoriesQuery,
	useLazyGetAllStudentsQuery,
	useLazyGetAllTutorialsQuery,
	useAssignLessonMutation,
	SelectDataViewModel,
} from "./lessonsApiSlice";

export enum LessonSteps {
	Lessons = 1,
	Students,
}

const initialSteps = [
	{ id: LessonSteps.Lessons, name: "Choose Lessons", status: StepStatus.Current },
	{ id: LessonSteps.Students, name: "Select Students", status: StepStatus.Upcoming },
];

const lockedSteps = [
	{ id: LessonSteps.Lessons, name: "Choose Lessons", status: StepStatus.Locked },
	{ id: LessonSteps.Students, name: "Select Students", status: StepStatus.Current },
];

interface Props {
	lockLesson: boolean;
	tutorialId?: number;
	setAlertMessage: (message: string) => void;
	onConfirm: () => void;
	onClose: () => void;
}

export default function AssignLessonForm(props: Props) {
	const [step, setStep] = useState(props.lockLesson ? LessonSteps.Students : LessonSteps.Lessons);
	const [steps, setSteps] = useState(props.lockLesson ? lockedSteps : initialSteps);
	const [tutorialIds, setTutorialIds] = useState<number[]>(
		props.tutorialId ? [props.tutorialId] : []
	);
	const [studentIds, setStudentIds] = useState<number[]>([]);

	const [disableConfirm, setDisableConfirm] = useState(props.lockLesson);
	const [cancelButtonLabel, setCancelButtonLabel] = useState("Cancel");
	const [confirmButtonLabel, setConfirmButtonLabel] = useState(
		props.lockLesson ? "Assign Selected" : "Next Step"
	);

	const [assignLesson] = useAssignLessonMutation();

	function updateSteps() {
		const tutorialComplete = tutorialIds.length > 0;
		const studentComplete = studentIds.length > 0;
		setSteps(
			steps.map((s) =>
				s.id === step
					? {
							...s,
							status:
								(s.id === LessonSteps.Lessons && tutorialComplete) ||
								(s.id === LessonSteps.Students && studentComplete)
									? StepStatus.Complete
									: StepStatus.Current,
						}
					: {
							...s,
							status:
								(s.id === LessonSteps.Lessons && tutorialComplete) ||
								(s.id === LessonSteps.Students && studentComplete)
									? props.lockLesson && s.id === LessonSteps.Lessons
										? StepStatus.Locked
										: StepStatus.Complete
									: StepStatus.Upcoming,
						}
			)
		);
	}

	function updateStudentCheckedIds(ids: number[]) {
		setStudentIds(ids);
	}

	function updateTutorialCheckedIds(ids: number[]) {
		setTutorialIds(ids);
	}

	function handleConfirm() {
		if (step === LessonSteps.Students) {
			if (tutorialIds.length > 0 && studentIds.length > 0) {
				assignLesson({ tutorialIds, studentIds })
					.unwrap()
					.then(() => {
						props.setAlertMessage("");
						props.onConfirm();
						props.onClose();
					});
			}
		} else {
			setStep(step + 1);
		}
	}

	function handleCancel() {
		if (step === LessonSteps.Lessons || props.lockLesson) {
			props.onClose();
		} else {
			setStep(step - 1);
		}
	}

	useEffect(() => {
		if (step === LessonSteps.Lessons) {
			setDisableConfirm(false);
			setConfirmButtonLabel("Next Step");
			setCancelButtonLabel("Cancel");
		} else if (step === LessonSteps.Students) {
			setDisableConfirm(studentIds.length == 0 || tutorialIds.length == 0);
			setConfirmButtonLabel("Assign Selected");
			setCancelButtonLabel(props.lockLesson ? "Cancel" : "Previous Step");
		}
		updateSteps();
	}, [step]);

	useEffect(() => {
		if (step === LessonSteps.Students) {
			setDisableConfirm(studentIds.length == 0 || tutorialIds.length == 0);
		}
	}, [studentIds]);

	useEffect(() => {
		updateSteps();
	}, [studentIds, tutorialIds]);

	return (
		<div>
			<StepProgressBar
				activeStep={steps.find((s) => s.id === step)}
				steps={steps}
				setStep={setStep}
				useLargeMediaBreakpoint={false}
			/>
			{step === LessonSteps.Lessons && (
				<SelectionSection
					isTutorialStep={true}
					checkedIds={tutorialIds}
					updateCheckedIdsCallback={updateTutorialCheckedIds}></SelectionSection>
			)}
			{step === LessonSteps.Students && (
				<SelectionSection
					isTutorialStep={false}
					lessonSelected={props.tutorialId}
					checkedIds={studentIds}
					updateCheckedIdsCallback={updateStudentCheckedIds}></SelectionSection>
			)}
			<div className="mt-3 grid grid-cols-2 gap-3">
				<button
					type="button"
					id="cancel-button"
					onClick={handleCancel}
					onKeyDown={(e) => handleEnterKey(e, handleCancel)}
					className="btn btn-neutral">
					{cancelButtonLabel}
				</button>
				<button
					type="button"
					id="confirm-button"
					disabled={disableConfirm}
					data-autofocus
					onClick={handleConfirm}
					onKeyDown={(e) => handleEnterKey(e, handleConfirm)}
					className="btn btn-primary">
					{confirmButtonLabel}
				</button>
			</div>
		</div>
	);
}

interface SelectionSectionProps {
	isTutorialStep: boolean;
	lessonSelected?: number;
	checkedIds: number[];
	updateCheckedIdsCallback: (checkedIds: number[]) => void;
}

function SelectionSection(props: SelectionSectionProps) {
	const _displayLength = 5;
	const initQueryargs: SelectQueryModel = {
		search_Filter: "",
		level_Filter: "",
		iDisplayStart: 0,
		iDisplayLength: _displayLength,
		iSortCol_0: 0,
		sSortDir_0: "desc",
	};

	const [queryargs, setQueryargs] = useState(initQueryargs);
	const [page, setPage] = useState(0);
	const [searchFilter, setSearchFilter] = useState("");
	const [dropdownFilter, setDropdownFilter] = useState("");
	const [dropdownOptions, setDropdownOptions] = useState<SimpleOption[]>([]);

	const [allData, setAllData] = useState<DataTablesViewModel<SelectDataViewModel>>();
	const [allDataLength, setAllDataLength] = useState(0);
	const [allChecked, setAllChecked] = useState(false);
	const [allPageChecked, setAllPageChecked] = useState(false);
	const [checkedIds, setCheckedIds] = useState<number[]>(props.checkedIds);
	const [allPageCheckboxIds, setAllPageCheckboxIds] = useState<number[]>([]);

	const categoriesResult = useGetCategoriesQuery();
	const categories = categoriesResult.data;

	const gradesResult = useGetGradesQuery();
	const grades = gradesResult.data;

	const [callGetAllTutorials, callGetAllTutorialsResult] = useLazyGetAllTutorialsQuery();
	const [callGetAllStudents, callGetAllStudentsResult] = useLazyGetAllStudentsQuery();

	function handleCheckboxChange(e: ChangeEvent<HTMLInputElement>) {
		if (e.target.checked) {
			setCheckedIds([...checkedIds, Number(e.target.value)]);
		} else {
			setCheckedIds(checkedIds.filter((item) => item !== Number(e.target.value)));
		}
	}

	function handleSelectAll(e: ChangeEvent<HTMLInputElement>) {
		if (e.target.checked) {
			const idsToAdd: number[] = [];
			allPageCheckboxIds.map((id) => {
				if (!checkedIds.includes(id)) {
					idsToAdd.push(id);
				}
			});
			setCheckedIds([...checkedIds, ...idsToAdd]);
		} else {
			setCheckedIds(checkedIds.filter((item) => !allPageCheckboxIds.includes(item)));
		}
	}

	const handlePageChange = (page: number) => {
		setPage(page);
	};

	function updatePageSelectAllCheckbox() {
		if (allPageCheckboxIds.every((id) => checkedIds.includes(id))) {
			setAllPageChecked(true);
		} else {
			setAllPageChecked(false);
		}
	}

	function updateQueryArgs(startValue: number) {
		setQueryargs({
			...queryargs,
			iDisplayStart: startValue,
			level_Filter: dropdownFilter,
			search_Filter: searchFilter,
		});
	}

	useEffect(() => {
		updateQueryArgs(page * _displayLength);
	}, [page]);

	useEffect(() => {
		setPage(0);
		updateQueryArgs(0);
	}, [searchFilter, dropdownFilter]);

	useEffect(() => {
		if (props.isTutorialStep && queryargs) {
			callGetAllTutorials(queryargs, true)
				.unwrap()
				.then((result) => {
					setAllData(result);
				})
				.catch((error: any) => {
					console.log(error);
				});
		} else if (!props.isTutorialStep && queryargs) {
			callGetAllStudents({ model: queryargs, tutorialId: props.lessonSelected }, true)
				.unwrap()
				.then((result) => {
					setAllData(result);
				})
				.catch((error: any) => {
					console.log(error);
				});
		}
	}, [queryargs]);

	useEffect(() => {
		props.updateCheckedIdsCallback(checkedIds);

		if (checkedIds.length === allDataLength && allDataLength != 0) {
			setAllChecked(true);
		} else {
			setAllChecked(false);
		}
		updatePageSelectAllCheckbox();
	}, [checkedIds]);

	useEffect(() => {
		updatePageSelectAllCheckbox();
	}, [allPageCheckboxIds]);

	useEffect(() => {
		const _temp: number[] = [];

		allData?.aaData.map((row) => {
			_temp.push(Number(row.dT_RowId));
		});

		setAllPageCheckboxIds(_temp);
		setAllDataLength(allData?.iTotalDisplayRecords ?? 0);
	}, [allData]);

	useEffect(() => {
		const categoryOptions: SimpleOption[] = [{ value: "", label: "All Categories" }];
		if (categories) {
			categories?.map((category) => {
				categoryOptions.push({
					value: category.categoryId.toString(),
					label: category.name,
				});
			});
		}

		const gradeOptions: SimpleOption[] = [{ value: "", label: "All Grades" }];
		if (grades) {
			grades?.map((grade) => {
				gradeOptions.push({ value: grade.gradeId.toString(), label: grade.name });
			});
		}

		setDropdownFilter(
			props.isTutorialStep
				? categoryOptions[0].value.toString()
				: gradeOptions[0].value.toString()
		);
		setDropdownOptions(props.isTutorialStep ? categoryOptions : gradeOptions);
	}, [categories, grades]);

	return (
		<div>
			<p className="font-semibold text-sm pt-2">Filters:</p>
			<div className="mt-1 flex flex-row gap-2">
				<SearchFilter
					id="searchbar"
					name="searchbar"
					htmlFor="searchbar"
					searchFilter={searchFilter}
					setSearchFilter={setSearchFilter}
					maxLength={125}
					label={props.isTutorialStep ? "Search Lessons" : "Search Students"}
					hiddenLabel
					placeHolder={props.isTutorialStep ? "Search Lessons" : "Search Students"}
					className="w-1/2"
					debounceDelay
				/>
				<Select
					value={dropdownFilter}
					setValue={(v) => setDropdownFilter(v as string)}
					simpleListBoxId="dropdownFilter"
					options={dropdownOptions}
					fieldClasses="w-1/2"
					simpleListBoxClasses="w-full"
				/>
			</div>
			<div className="mt-1">
				<div className="flex justify-between items-end">
					<p className="font-semibold text-sm p-1">
						{checkedIds.length} {props.isTutorialStep ? "lessons" : "students"} selected
					</p>
					<button
						className="text-xs p-1 hover:cursor-pointer focus:outline-hidden"
						onClick={() => setCheckedIds([])}>
						[clear all]
					</button>
				</div>
				<table className="tbl table-fixed tbl-data">
					<thead>
						<tr>
							<th className="w-1/12">
								<input
									id="selectAllCheckbox"
									type="checkbox"
									className="size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
									onChange={(e) => handleSelectAll(e)}
									checked={allChecked || allPageChecked}
								/>
							</th>
							<th className={props.isTutorialStep ? "w-7/12" : "w-6/12"}>Name</th>
							<th className={props.isTutorialStep ? "w-4/12" : "w-5/12"}>
								{props.isTutorialStep ? "Level" : "Username"}
							</th>
						</tr>
					</thead>

					{allData?.aaData.length == 0 ? (
						<NoDataTableBody columnCount={3} message="No matching records found" />
					) : (
						<tbody>
							{allData?.aaData.map((row) => {
								return (
									<tr
										key={
											(props.isTutorialStep ? "lesson-" : "student-") +
											row.dT_RowId
										}>
										<td>
											<input
												id={
													(props.isTutorialStep
														? "lesson-"
														: "student-") + row.dT_RowId
												}
												value={row.dT_RowId}
												type="checkbox"
												checked={checkedIds.includes(Number(row.dT_RowId))}
												className="size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
												onChange={(e) => handleCheckboxChange(e)}
											/>
										</td>
										<td>
											<TextWithTip
												uniqueName={`name-${row.dT_RowId}`}
												text={row.name}
											/>
										</td>
										<td>
											<TextWithTip
												uniqueName={
													(props.isTutorialStep
														? "level-"
														: "username-") + row.dT_RowId
												}
												text={
													props.isTutorialStep ? row.level : row.username
												}
											/>
										</td>
									</tr>
								);
							})}
						</tbody>
					)}

					{(allData?.iTotalDisplayRecords ?? 0) > queryargs.iDisplayLength && (
						<tfoot>
							<tr>
								<td colSpan={3} className="!px-2">
									<Pagination
										itemCount={allData?.iTotalDisplayRecords}
										page={page}
										pageSize={queryargs.iDisplayLength}
										onChange={(page) => handlePageChange(page)}
									/>
								</td>
							</tr>
						</tfoot>
					)}
				</table>
			</div>
		</div>
	);
}

interface PaginationProps {
	pageSize: number;
	page: number;
	itemCount: number | undefined;
	onChange: (page: number) => void;
}

function Pagination({ pageSize, page, itemCount, onChange }: PaginationProps) {
	if (!itemCount || itemCount <= pageSize) {
		return null;
	}
	const pages = Math.ceil(itemCount / pageSize);
	const first = page * pageSize + 1;
	const last = (page + 1) * pageSize > itemCount ? itemCount : (page + 1) * pageSize;

	return (
		<div className="flex items-center justify-between bg-white -mb-3">
			<div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
				<div>
					<p className="text-sm text-gray-700">
						Showing <span className="font-medium">{first}</span> to{" "}
						<span className="font-medium">{last}</span> of{" "}
						<span className="font-medium">{itemCount}</span> results
					</p>
				</div>
				<div>
					<nav
						className="isolate inline-flex space-x-1 rounded-md shadow-sm"
						aria-label="Pagination">
						<button
							className="btn btn-naked border !shadow-md"
							disabled={page <= 0}
							onClick={() => onChange(--page)}>
							<ChevronLeftIcon className="size-4" />
						</button>
						<button
							className="btn btn-naked border !shadow-md"
							disabled={page >= pages - 1}
							onClick={() => onChange(++page)}>
							<ChevronRightIcon className="size-4" />
						</button>
					</nav>
				</div>
			</div>
		</div>
	);
}
